import React, { useEffect, useState } from 'react';
import { API_URLS } from '../../../config/api';
import { Plus, Trash2, Edit2, Eye, EyeOff, X as XIcon, Star, CheckCircle } from 'lucide-react';
import { Switch } from '../../ui/switch';
import { Button } from '../../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../ui/dialog';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Textarea } from '../../ui/textarea';
import { Badge } from '../../ui/badge';

export default function AdminCorporatePackages() {
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState({ 
    id: '', 
    name: '', 
    slug: '',
    description: '',
    oldPrice: '', 
    price: '', 
    features: [], 
    featureInput: '', 
    is_active: true, 
    is_popular: false, 
    sort_order: 0,
    url: ''
  });

  const fetchPackages = async () => {
    setLoading(true);
    try {
      const res = await fetch(API_URLS.ADMIN.CORPORATE_PACKAGES);
      const data = await res.json();
      if (data.status === 'success') {
        const normalized = (data.data || []).map((p) => ({
          id: p.id,
          name: p.name ?? '',
          slug: p.slug ?? '',
          description: p.description ?? '',
          oldPrice: p.old_price ?? '',
          price: p.price ?? '',
          features: Array.isArray(p.features) ? p.features : [],
          is_active: !!p.is_active,
          is_popular: !!p.is_popular,
          sort_order: p.sort_order ?? 0
        }));
        setPackages(normalized);
      }
    } catch (error) {
      console.error('Error fetching packages:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchPackages(); }, []);

  const openCreate = () => {
    setForm({ 
      id: '', 
      name: '', 
      slug: '',
      description: '',
      oldPrice: '', 
      price: '', 
      features: [], 
      featureInput: '', 
      is_active: true, 
      is_popular: false, 
      sort_order: 0,
      url: ''
    });
    setShowModal(true);
  };

  const openEdit = (p) => {
    // Normalize features to { name, included } format
    const normalizedFeatures = Array.isArray(p.features) 
      ? p.features.map(f => {
          if (typeof f === 'string') {
            return { name: f, included: true };
          } else if (f && typeof f === 'object') {
            return { 
              name: f.name ?? JSON.stringify(f), 
              included: f.included !== false 
            };
          }
          return { name: String(f), included: true };
        })
      : [];
    
    setForm({
      id: p.id,
      name: p.name || '',
      slug: p.slug || '',
      description: p.description || '',
      oldPrice: p.oldPrice || '',
      price: p.price || '',
      features: normalizedFeatures,
      url: p.url || '',
      featureInput: '',
      is_active: !!p.is_active,
      is_popular: !!p.is_popular,
      sort_order: typeof p.sort_order === 'number' ? p.sort_order : parseInt(p.sort_order || 0, 10)
    });
    setShowModal(true);
  };

  const savePackage = async () => {
    const payload = {
      id: form.id,
      name: form.name,
      slug: form.slug,
      description: form.description,
      old_price: form.oldPrice,
      price: form.price,
      features: (form.features || []).map(f => {
        if (typeof f === 'string') {
          return { name: f, included: true };
        } else if (f && typeof f === 'object') {
          return { 
            name: f.name ?? String(f), 
            included: f.included !== false 
          };
        }
        return { name: String(f), included: true };
      }).filter(f => f.name),
      is_active: form.is_active,
      is_popular: form.is_popular,
      sort_order: Number.isFinite(form.sort_order) ? form.sort_order : parseInt(form.sort_order || 0, 10)
    };
    
    const url = form.id
      ? `${API_URLS.ADMIN.UPDATE_CORPORATE_PACKAGE}&id=${encodeURIComponent(form.id)}`
      : API_URLS.ADMIN.CREATE_CORPORATE_PACKAGE;
    
    try {
      const res = await fetch(url, { 
        method: 'POST', 
        headers: { 'Content-Type': 'application/json' }, 
        body: JSON.stringify(payload) 
      });
      const result = await res.json();
      
      if (res.ok && result.status === 'success') {
        setShowModal(false);
        fetchPackages();
      } else {
        alert(result.message || 'Güncelleme başarısız oldu');
      }
    } catch (err) {
      alert('Bağlantı hatası, lütfen tekrar deneyin');
    }
  };

  const addFeature = () => {
    const val = form.featureInput.trim();
    if (!val) return;
    setForm(prev => ({ 
      ...prev, 
      features: [...(prev.features || []), { name: val, included: true }], 
      featureInput: '' 
    }));
  };

  const removeFeature = (idx) => {
    setForm(prev => ({ ...prev, features: prev.features.filter((_, i) => i !== idx) }));
  };

  const toggleFeatureIncluded = (idx) => {
    setForm(prev => ({
      ...prev,
      features: prev.features.map((f, i) => 
        i === idx ? { ...f, included: !f.included } : f
      )
    }));
  };

  const deletePackage = async (id) => {
    if (!window.confirm('Bu paketi silmek istediğinizden emin misiniz?')) return;
    try {
      const res = await fetch(`${API_URLS.ADMIN.DELETE_CORPORATE_PACKAGE}&id=${id}`, { method: 'DELETE' });
      const result = await res.json();
      if (result.status === 'success') {
        fetchPackages();
      }
    } catch (err) {
      alert('Silme hatası');
    }
  };

  const toggleStatus = async (id) => {
    try {
      const res = await fetch(`${API_URLS.ADMIN.TOGGLE_CORPORATE_PACKAGE_STATUS}&id=${id}`, { method: 'POST' });
      const result = await res.json();
      if (result.status === 'success') {
        fetchPackages();
      }
    } catch (err) {
      alert('Durum değiştirme hatası');
    }
  };

  const togglePopular = async (id) => {
    try {
      const res = await fetch(`${API_URLS.ADMIN.TOGGLE_CORPORATE_PACKAGE_POPULAR}&id=${id}`, { method: 'POST' });
      const result = await res.json();
      if (result.status === 'success') {
        fetchPackages();
      }
    } catch (err) {
      alert('Popüler durum değiştirme hatası');
    }
  };

  const generateSlug = (name) => {
    return name
      .toLowerCase()
      .replace(/ğ/g, 'g')
      .replace(/ü/g, 'u')
      .replace(/ş/g, 's')
      .replace(/ı/g, 'i')
      .replace(/ö/g, 'o')
      .replace(/ç/g, 'c')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  };

  const handleNameChange = (name) => {
    setForm({
      ...form,
      name,
      slug: generateSlug(name)
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600 mb-2"></div>
          <p className="text-gray-600">Paketler yükleniyor...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Kurumsal Web Sitesi Paketleri</h1>
          <p className="text-gray-600 mt-2">Kurumsal web sitesi paketlerini yönetin</p>
        </div>
        <Button
          onClick={openCreate}
          className="bg-purple-600 hover:bg-purple-700 text-white"
        >
          <Plus className="w-4 h-4 mr-2" />
          Yeni Paket
        </Button>
      </div>

      {/* Packages List */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {packages.map((pkg) => (
          <Card key={pkg.id} className="hover:shadow-lg transition-shadow">
            <CardHeader className="pb-3">
              <div className="flex items-start justify-between">
                <div>
                  <CardTitle className="text-lg flex items-center gap-2">
                    {pkg.name}
                    {pkg.is_popular && (
                      <Badge className="bg-purple-500 text-white">
                        <Star className="w-3 h-3 mr-1" />
                        Popüler
                      </Badge>
                    )}
                  </CardTitle>
                  <p className="text-sm text-gray-500 mt-1">/{pkg.slug}</p>
                </div>
                <Badge variant={pkg.is_active ? 'success' : 'secondary'}>
                  {pkg.is_active ? 'Aktif' : 'Pasif'}
                </Badge>
              </div>
            </CardHeader>
            <CardContent>
              {pkg.description && (
                <p className="text-sm text-gray-600 mb-4 line-clamp-2">{pkg.description}</p>
              )}
              
              <div className="mb-4">
                {pkg.oldPrice && (
                  <span className="text-gray-400 line-through mr-2">₺{pkg.oldPrice}</span>
                )}
                <span className="text-2xl font-bold text-purple-600">₺{pkg.price}</span>
              </div>

              <div className="space-y-2 mb-4">
                <p className="text-sm font-semibold text-gray-700">Özellikler:</p>
                {pkg.features.slice(0, 3).map((feature, idx) => {
                  const featureName = typeof feature === 'string' ? feature : (feature?.name || '');
                  const included = typeof feature === 'object' ? feature.included !== false : true;
                  return (
                    <div key={idx} className="flex items-start text-sm">
                      {included ? (
                        <CheckCircle className="w-4 h-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                      ) : (
                        <XIcon className="w-4 h-4 text-gray-400 mr-2 mt-0.5 flex-shrink-0" />
                      )}
                      <span className={included ? 'text-gray-700' : 'text-gray-400 line-through'}>
                        {featureName}
                      </span>
                    </div>
                  );
                })}
                {pkg.features.length > 3 && (
                  <p className="text-xs text-gray-500">+{pkg.features.length - 3} daha...</p>
                )}
              </div>

              <div className="flex items-center justify-between pt-3 border-t">
                <span className="text-sm text-gray-500">Sıra: {pkg.sort_order}</span>
                <div className="flex space-x-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => toggleStatus(pkg.id)}
                    title={pkg.is_active ? 'Pasif Yap' : 'Aktif Yap'}
                  >
                    {pkg.is_active ? (
                      <EyeOff className="w-4 h-4" />
                    ) : (
                      <Eye className="w-4 h-4" />
                    )}
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => togglePopular(pkg.id)}
                    title={pkg.is_popular ? 'Popüler Kaldır' : 'Popüler Yap'}
                  >
                    <Star className={`w-4 h-4 ${pkg.is_popular ? 'fill-yellow-400 text-yellow-400' : ''}`} />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => openEdit(pkg)}
                  >
                    <Edit2 className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => deletePackage(pkg.id)}
                  >
                    <Trash2 className="w-4 h-4 text-red-600" />
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {packages.length === 0 && (
        <Card className="p-12 text-center">
          <div className="text-gray-400 mb-4">
            <Star className="w-16 h-16 mx-auto" />
          </div>
          <h3 className="text-xl font-semibold text-gray-700 mb-2">Henüz paket yok</h3>
          <p className="text-gray-500 mb-4">İlk kurumsal paketinizi oluşturun</p>
          <Button onClick={openCreate} className="bg-purple-600 hover:bg-purple-700">
            <Plus className="w-4 h-4 mr-2" />
            Paket Ekle
          </Button>
        </Card>
      )}

      {/* Form Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-[95vw] sm:max-w-md md:max-w-2xl lg:max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-base sm:text-lg">
              {form.id ? 'Paket Düzenle' : 'Yeni Paket Ekle'}
            </DialogTitle>
          </DialogHeader>
          
          <form onSubmit={(e) => { e.preventDefault(); savePackage(); }} className="space-y-4 sm:space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
              <div>
                <Label htmlFor="name" className="text-sm">Paket Adı *</Label>
                <Input
                  id="name"
                  value={form.name}
                  onChange={(e) => handleNameChange(e.target.value)}
                  placeholder="Profesyonel"
                  required
                  className="w-full"
                />
              </div>

              <div>
                <Label htmlFor="slug" className="text-sm">Slug *</Label>
                <Input
                  id="slug"
                  value={form.slug}
                  onChange={(e) => setForm({ ...form, slug: e.target.value })}
                  placeholder="profesyonel"
                  required
                  className="w-full"
                />
              </div>

              <div className="md:col-span-2">
                <Label htmlFor="description" className="text-sm">Açıklama</Label>
                <Textarea
                  id="description"
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  placeholder="Paket açıklaması..."
                  rows={3}
                  className="w-full"
                />
              </div>

              <div className="md:col-span-2">
                <Label htmlFor="url" className="text-sm">URL (Paket Sayfası Linki)</Label>
                <Input
                  id="url"
                  type="text"
                  value={form.url || ''}
                  onChange={(e) => setForm({ ...form, url: e.target.value })}
                  placeholder="/kurumsal veya https://example.com/paket"
                  className="w-full"
                />
                <p className="text-xs text-gray-500 mt-1 break-words">Paket detay sayfası veya satın alma sayfası URL'si</p>
              </div>

              <div>
                <Label htmlFor="oldPrice" className="text-sm">Eski Fiyat (₺)</Label>
                <Input
                  id="oldPrice"
                  type="number"
                  value={form.oldPrice}
                  onChange={(e) => setForm({ ...form, oldPrice: e.target.value })}
                  placeholder="12999"
                  className="w-full"
                />
              </div>

              <div>
                <Label htmlFor="price" className="text-sm">Fiyat (₺) *</Label>
                <Input
                  id="price"
                  type="number"
                  value={form.price}
                  onChange={(e) => setForm({ ...form, price: e.target.value })}
                  placeholder="9999"
                  required
                  className="w-full"
                />
              </div>

              <div>
                <Label htmlFor="sort_order" className="text-sm">Sıralama</Label>
                <Input
                  id="sort_order"
                  type="number"
                  value={form.sort_order}
                  onChange={(e) => setForm({ ...form, sort_order: parseInt(e.target.value) || 0 })}
                  min="0"
                  className="w-full"
                />
              </div>

              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:space-x-4">
                <div className="flex items-center space-x-2">
                  <Switch
                    id="is_active"
                    checked={form.is_active}
                    onCheckedChange={(checked) => setForm({ ...form, is_active: checked })}
                  />
                  <Label htmlFor="is_active" className="text-sm">Aktif</Label>
                </div>
                
                <div className="flex items-center space-x-2">
                  <Switch
                    id="is_popular"
                    checked={form.is_popular}
                    onCheckedChange={(checked) => setForm({ ...form, is_popular: checked })}
                  />
                  <Label htmlFor="is_popular" className="text-sm">Popüler</Label>
                </div>
              </div>
            </div>

            {/* Features Section */}
            <div className="space-y-4">
              <div>
                <Label className="text-sm">Özellikler</Label>
                <div className="flex flex-col sm:flex-row gap-2 mt-2">
                  <Input
                    value={form.featureInput}
                    onChange={(e) => setForm({ ...form, featureInput: e.target.value })}
                    placeholder="Yeni özellik ekle..."
                    onKeyPress={(e) => { if (e.key === 'Enter') { e.preventDefault(); addFeature(); } }}
                    className="flex-1 min-w-0"
                  />
                  <Button type="button" onClick={addFeature} className="bg-purple-600 hover:bg-purple-700 flex-shrink-0 whitespace-nowrap">
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
              </div>

              {form.features.length > 0 && (
                <div className="space-y-2">
                  {form.features.map((feature, idx) => {
                    const featureName = typeof feature === 'string' ? feature : (feature?.name || '');
                    const included = typeof feature === 'object' ? feature.included !== false : true;
                    
                    return (
                      <div key={idx} className="flex flex-col sm:flex-row items-start sm:items-center gap-2 sm:gap-2 p-2 bg-gray-50 rounded">
                        <div className="flex items-center gap-2 flex-1 w-full sm:w-auto min-w-0">
                          <Switch
                            checked={included}
                            onCheckedChange={() => toggleFeatureIncluded(idx)}
                            className="flex-shrink-0"
                          />
                          <span className={`flex-1 min-w-0 break-words text-sm ${!included ? 'line-through text-gray-400' : 'text-gray-700'}`}>
                            {featureName}
                          </span>
                        </div>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => removeFeature(idx)}
                          className="flex-shrink-0 self-end sm:self-auto"
                        >
                          <XIcon className="w-4 h-4 text-red-600" />
                        </Button>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Feature Preview */}
              {form.features.length > 0 && (
                <div className="mt-4 p-4 bg-purple-50 rounded-lg">
                  <p className="text-sm font-semibold text-purple-900 mb-3">Özellik Önizleme:</p>
                  <div className="space-y-2">
                    {form.features.map((feature, idx) => {
                      const featureName = typeof feature === 'string' ? feature : (feature?.name || '');
                      const included = typeof feature === 'object' ? feature.included !== false : true;
                      
                      return (
                        <div key={idx} className="flex items-start">
                          {included ? (
                            <CheckCircle className="w-4 h-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                          ) : (
                            <XIcon className="w-4 h-4 text-gray-400 mr-2 mt-0.5 flex-shrink-0" />
                          )}
                          <span className={`text-sm ${included ? 'text-gray-700' : 'text-gray-400 line-through'}`}>
                            {featureName}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>

            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:space-x-4 pt-4">
              <Button type="submit" className="bg-purple-600 hover:bg-purple-700 text-white w-full sm:w-auto">
                {form.id ? 'Güncelle' : 'Ekle'}
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowModal(false)}
                className="w-full sm:w-auto"
              >
                İptal
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

